/**
 * Quick-add from a product card.
 *
 * Two paths, decided in Liquid (see snippets/cs-product-card.liquid):
 *
 *   one variant   the button adds straight to the cart
 *   options       the button opens the quick-add dialog, which fetches the
 *                 product's variants on demand and asks
 *
 * Adds go through /cart/add.js rather than a form post, for the same reason the
 * product form was moved onto it: Shopify's Web Pixels instrument the Ajax cart
 * API, so an Ajax add emits `product_added_to_cart` and a native form post does
 * not. That signal is what GA4 and Meta optimise on, and it was missing from
 * this store for months. Every add on the site now takes the same road.
 *
 * Confirmation is not this file's job — it hands off to cs-cart-toast.js, which
 * works out what landed in the cart and says so. One toast, one implementation,
 * whichever button was pressed.
 */
(function () {
  const CART_ADD_URL = window.Theme?.routes?.cart_add_url || '/cart/add.js';

  /** Swap a quick-add button through busy -> done -> back, so the click has an
   *  answer at the point of the click as well as in the toast.
   *
   *  Two shapes wear this: the icon-only square in a tile's corner, and the
   *  full Add to cart button in the featured section, which also carries a
   *  [data-cs-quick-add-label]. The label is optional, so both work. */
  const flash = (button, state) => {
    const icon = button.querySelector('[data-cs-quick-add-icon]');
    const label = button.querySelector('[data-cs-quick-add-label]');
    if (!icon) return;

    if (label && !label.dataset.idle) label.dataset.idle = label.textContent;

    // The corner square draws a 20px glyph; the featured button sets its own
    // through data-icon-size, because a 20px icon beside 14px type is a lump.
    const size = button.dataset.iconSize || 'w-5 h-5';

    if (state === 'busy') {
      button.dataset.busy = 'true';
      if (label) label.textContent = 'Adding…';
      icon.innerHTML =
        '<svg class="' + size + '" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" aria-hidden="true"><path d="M21 12a9 9 0 1 1-6.219-8.56"/></svg>';
      return;
    }

    delete button.dataset.busy;

    if (state === 'done') {
      if (label) label.textContent = 'Added';
      icon.innerHTML =
        '<svg class="' + size + '" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M20 6 9 17l-5-5"/></svg>';
      setTimeout(() => flash(button, 'idle'), 1600);
      return;
    }

    if (label) label.textContent = label.dataset.idle;

    if (state === 'error') button.classList.add('animate-nudge-x');
    setTimeout(() => button.classList.remove('animate-nudge-x'), 400);

    icon.innerHTML =
      '<svg class="' + size + '" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 12h14"/><path d="M12 5v14"/></svg>';
  };

  /**
   * @param {number|string} id variant id
   * @param {number} quantity
   * @returns {Promise<void>} rejects with a human-readable message
   */
  const addToCart = async (id, quantity) => {
    const response = await fetch(CART_ADD_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: Number(id), quantity: Number(quantity) || 1 }] }),
    });

    const payload = await response.json().catch(() => null);

    if (!response.ok) {
      // Shopify's own wording is better than anything invented here — it knows
      // how many are left and whether the variant is gone.
      throw new Error(payload?.description || payload?.message || 'Could not add that to your cart.');
    }

    // The toast reads the settled cart itself; nothing here needs the response.
    if (window.csCartToast) await window.csCartToast.announce();
    else if (typeof window.csPaintCartCount === 'function') {
      const cart = await fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());
      window.csPaintCartCount(cart.item_count);
    }
  };

  // --- Dialog ---------------------------------------------------------------

  const dialog = document.querySelector('[data-cs-qa]');

  const qa = dialog && {
    panel: dialog.querySelector('[data-cs-qa-panel]'),
    scrim: dialog.querySelector('[data-cs-qa-scrim]'),
    close: dialog.querySelector('[data-cs-qa-close]'),
    media: dialog.querySelector('[data-cs-qa-media]'),
    image: dialog.querySelector('[data-cs-qa-image]'),
    title: dialog.querySelector('[data-cs-qa-title]'),
    price: dialog.querySelector('[data-cs-qa-price]'),
    status: dialog.querySelector('[data-cs-qa-status]'),
    options: dialog.querySelector('[data-cs-qa-options]'),
    qtyInput: dialog.querySelector('[data-cs-qa-qty-input]'),
    add: dialog.querySelector('[data-cs-qa-add]'),
    addLabel: dialog.querySelector('[data-cs-qa-add-label]'),
    error: dialog.querySelector('[data-cs-qa-error]'),
    details: dialog.querySelector('[data-cs-qa-details]'),
  };

  /** @type {{product: any, selected: Record<string, string>, trigger: HTMLElement|null}} */
  const state = { product: null, selected: {}, trigger: null };

  /**
   * /products/<handle>.js has returned `options` as both an array of strings and
   * an array of {name, position, values} depending on the platform version.
   * Normalise once so the render below only has one shape to think about.
   */
  const readOptions = (product) =>
    (product.options || []).map((option, index) =>
      typeof option === 'string'
        ? { name: option, position: index + 1, values: [] }
        : { name: option.name, position: option.position || index + 1, values: option.values || [] }
    );

  const optionValues = (product, position) => {
    const seen = [];
    product.variants.forEach((variant) => {
      const value = variant.options[position - 1];
      if (value != null && !seen.includes(value)) seen.push(value);
    });
    return seen;
  };

  /**
   * Whether a value is purchasable given the other current selections.
   * Same rule as cs-product.js, which is the point — the dialog must not
   * disagree with the product page about what is in stock.
   */
  const valueAvailable = (position, value) =>
    state.product.variants.some(
      (variant) =>
        variant.available &&
        variant.options.every((option, index) =>
          index + 1 === position ? option === value : state.selected[index + 1] === option
        )
    );

  const currentVariant = () =>
    state.product?.variants.find((variant) =>
      variant.options.every((option, index) => state.selected[index + 1] === option)
    ) || null;

  const money = (cents) =>
    typeof window.csFormatMoney === 'function' ? window.csFormatMoney(cents) : '';

  const paint = () => {
    const variant = currentVariant();

    dialog.querySelectorAll('[data-cs-qa-value]').forEach((button) => {
      const position = Number(button.dataset.optionPosition);
      const value = button.dataset.value;
      const on = state.selected[position] === value;
      const off = !valueAvailable(position, value);

      button.setAttribute('aria-pressed', String(on));
      button.classList.toggle('bg-foreground', on);
      button.classList.toggle('text-background', on);
      button.classList.toggle('border-foreground', on);
      button.classList.toggle('border-border', !on);
      button.classList.toggle('btn-fill', !on);
      button.classList.toggle('hover:text-primary-foreground', !on);
      button.classList.toggle('line-through', off && !on);
      button.classList.toggle('opacity-40', off && !on);
      button.title = off ? 'Unavailable with current selection' : '';
    });

    if (variant?.featured_image?.src) qa.image.src = variant.featured_image.src;

    qa.price.textContent = variant ? money(variant.price) : money(state.product.price);
    qa.add.disabled = !variant || !variant.available;
    qa.addLabel.textContent = !variant ? 'Unavailable' : variant.available ? 'Add to cart' : 'Sold out';
  };

  const render = (product) => {
    state.product = product;
    state.selected = {};

    // Open on the first variant anyone can actually buy, not on variant zero —
    // landing on a sold-out combination reads as a broken dialog.
    const start = product.variants.find((variant) => variant.available) || product.variants[0];
    start?.options.forEach((value, index) => {
      state.selected[index + 1] = value;
    });

    qa.title.textContent = product.title;
    qa.image.src = start?.featured_image?.src || product.featured_image || '';
    qa.media.hidden = !qa.image.src;

    // Built as a string rather than with createElement so Tailwind's scanner —
    // which reads assets/*.js, see tailwind.config.ts — sees these classes and
    // generates them. Values are escaped on the way in.
    qa.options.innerHTML = readOptions(product)
      .map((option) => {
        const values = option.values.length ? option.values : optionValues(product, option.position);
        const buttons = values
          .map(
            (value) =>
              '<button type="button" data-cs-qa-value data-option-position="' +
              option.position +
              '" data-value="' +
              String(value).replace(/"/g, '&quot;') +
              '" class="relative flex items-center gap-2 px-4 py-2 border text-sm transition-all hover:border-foreground">' +
              String(value).replace(/</g, '&lt;') +
              '</button>'
          )
          .join('');

        return (
          '<div class="mb-5 last:mb-0">' +
          '<p class="text-sm font-bold tracking-tight leading-none mb-3">' +
          String(option.name).replace(/</g, '&lt;') +
          '</p><div class="flex flex-wrap gap-2">' +
          buttons +
          '</div></div>'
        );
      })
      .join('');

    qa.status.hidden = true;
    qa.options.hidden = false;
    paint();
  };

  const setOpen = (open) => {
    dialog.hidden = false;
    const scroller = document.querySelector('.page-wrapper');

    if (open) {
      if (scroller) scroller.style.overflow = 'hidden';
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          dialog.dataset.state = 'open';
        });
      });
      // The close button is the safe landing spot: focusing an option button
      // would scroll a long option list before the customer has seen the top.
      setTimeout(() => qa.close.focus(), 60);
      return;
    }

    dialog.dataset.state = 'closed';
    if (scroller) scroller.style.overflow = '';
    state.trigger?.focus();
    state.trigger = null;

    const done = () => {
      if (dialog.dataset.state === 'closed') dialog.hidden = true;
      qa.panel.removeEventListener('transitionend', done);
    };
    qa.panel.addEventListener('transitionend', done);
    setTimeout(done, 400);
  };

  const open = async (button) => {
    if (!qa) {
      window.location.href = button.dataset.productUrl;
      return;
    }

    state.trigger = button;
    state.product = null;
    qa.status.hidden = false;
    qa.status.textContent = 'Loading options…';
    qa.options.hidden = true;
    qa.options.innerHTML = '';
    qa.error.hidden = true;
    qa.title.textContent = button.getAttribute('aria-label')?.replace(/^Choose options for /, '') || '';
    qa.price.textContent = '';
    qa.image.removeAttribute('src');
    qa.media.hidden = true;
    qa.qtyInput.value = '1';
    qa.add.disabled = true;
    qa.addLabel.textContent = 'Add to cart';
    qa.details.href = button.dataset.productUrl || '#';

    setOpen(true);

    try {
      const response = await fetch(button.dataset.productUrl + '.js', { headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error('Product fetch failed: ' + response.status);
      render(await response.json());
    } catch (error) {
      // A dialog that cannot load its options is worse than no dialog — send
      // them to the page that definitely works.
      console.warn('[cs-quick-add] falling back to the product page:', error);
      window.location.href = button.dataset.productUrl;
    }
  };

  // --- Wiring ---------------------------------------------------------------

  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-cs-quick-add]');
    if (!button) return;

    // The card is a stretched link; without this the click opens the product.
    event.preventDefault();
    event.stopPropagation();

    if (!button.dataset.variantId) {
      open(button);
      return;
    }

    flash(button, 'busy');
    addToCart(button.dataset.variantId, 1)
      .then(() => flash(button, 'done'))
      .catch((error) => {
        flash(button, 'error');
        console.warn('[cs-quick-add]', error);
      });
  });

  if (!qa) return;

  dialog.addEventListener('click', (event) => {
    if (event.target === qa.scrim) setOpen(false);

    const value = event.target.closest('[data-cs-qa-value]');
    if (value) {
      state.selected[Number(value.dataset.optionPosition)] = value.dataset.value;
      qa.error.hidden = true;
      paint();
    }
  });

  qa.close.addEventListener('click', () => setOpen(false));

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && dialog.dataset.state === 'open') setOpen(false);
    if (event.key !== 'Tab' || dialog.dataset.state !== 'open') return;

    // Keep Tab inside the dialog. aria-modal alone does not stop a keyboard
    // walking out into the page behind it.
    const focusable = [...qa.panel.querySelectorAll('a[href], button:not([disabled]), input, [tabindex]:not([tabindex="-1"])')]
      .filter((el) => el.offsetParent !== null);
    if (!focusable.length) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  });

  dialog.querySelectorAll('[data-cs-qa-qty]').forEach((button) => {
    button.addEventListener('click', () => {
      const step = Number(button.dataset.csQaQty);
      qa.qtyInput.value = String(Math.max(1, (parseInt(qa.qtyInput.value, 10) || 1) + step));
    });
  });

  qa.add.addEventListener('click', () => {
    const variant = currentVariant();
    if (!variant) return;

    qa.error.hidden = true;
    qa.add.disabled = true;
    qa.addLabel.textContent = 'Adding…';

    addToCart(variant.id, parseInt(qa.qtyInput.value, 10) || 1)
      .then(() => {
        qa.addLabel.textContent = 'Added';
        // Close on a beat, so the dialog's own confirmation is seen before the
        // toast takes over as the record of what happened.
        setTimeout(() => setOpen(false), 550);
      })
      .catch((error) => {
        qa.error.textContent = error.message;
        qa.error.hidden = false;
        qa.add.disabled = false;
        qa.addLabel.textContent = 'Add to cart';
      });
  });
})();
